import { useState, useEffect } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Switch from 'app/components/Switch';
import Dropdown from 'app/components/Dropdown';
import UserAvatar from 'app/components/UserAvatar';
import RadioBlock from 'app/components/RadioBlock';
import Button from 'app/components/Button/ResetedButton';
import Icon from 'app/components/Icon';
import arrowIcon from 'app/icons/arrow_long.svg';
import settingsIcon from 'app/icons/settings.svg';
import exportIcon from 'app/icons/export.svg';
import offIcon from 'app/icons/off.svg';
import PrivateContent from 'app/common/PrivateContent';
import { openInNewtab, useReadmeRequest } from 'app/components/AuthReadme/utils';
import CircleLoader from 'app/components/Loader/CircleLoader';
import themeLightIcon from 'app/icons/theme_light.svg';
import themeDarkIcon from 'app/icons/theme_dark.svg';
import checkIcon from 'app/icons/check.svg';
import usersIcon from 'app/icons/users.svg';
import { canSeeExternalProvidesVideogames } from 'app/utils/videogame';
import {
  COMPETITORS_LOGO,
  COMPETITORS_NAME,
  TOGGLABLE_COMPETITORS
} from 'app/config/competitors.config';
import Checkbox from 'app/components/Checkbox';
import { toggleValueInArray } from 'app/utils/array';

import style from './style.scss';

interface Props {
  collapsed: boolean;
  user: User;
  onLogout: () => void;
  updatePreferences: (params: Partial<UserPreferences>) => void;
}

const oddsFormatOptions: { label: string; value: OddsFormat }[] = [
  {
    label: 'Probability',
    value: 'probability'
  },
  {
    label: 'Decimal',
    value: 'decimal'
  },
  {
    label: 'American',
    value: 'american'
  }
];

const UserDropdown: FC<Props> = ({ collapsed, user, onLogout, updatePreferences }) => {
  const [t] = useTranslation();

  const {
    preferences,
    role,
    company: { odds_subscription: userType }
  } = user;

  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);
  const buttonClassName = classnames(style.button, {
    [style.button_collapsed]: collapsed
  });
  const [openedMenu, setOpenedMenu] = useState<'trading' | 'display' | null>(null);

  const { fetch: fetchDocUrl, status: docUrlStatus, data: docUrl } = useReadmeRequest('pandaodds');
  useEffect(() => {
    if (docUrlStatus === 'SUCCESS' && docUrl?.url) {
      openInNewtab(docUrl.url);
    }
  }, [docUrl]);

  return (
    <Dropdown
      onOpenChange={() => setDisplayDropdown(!displayDropdown)}
      opened={displayDropdown}
      alignment="start"
      variation="empty"
      className={buttonClassName}
      withIcon={!collapsed}
      label={
        <>
          <UserAvatar user={user} size={collapsed ? 25 : 20} />
          {!collapsed && (
            <span className={style.userNameContainer}>
              {userType === 'pandascore' ? user.name : user.email}
            </span>
          )}
        </>
      }
      width={openedMenu ? 645 : 370}
      portalClassName={style.portalOverride}
      dropdownClassName={style.dropdownContent}
      onMouseLeave={() => setOpenedMenu(null)}
      withPortal
    >
      <div className={style.leftPanel}>
        {/* TEMPORARY, will be available to book maker with the new bookmaker input feature */}
        <PrivateContent access={['pandascore']} userType={userType}>
          <Button
            onMouseEnter={() => setOpenedMenu('trading')}
            onFocus={() => setOpenedMenu('trading')}
            className={style.menuButton}
          >
            <Icon href={settingsIcon} className={style.menuButtonIcon} />
            <span>{t('top_bar-preferences-trading')}</span>
            <Icon href={arrowIcon} className={style.arrowIcon} />
          </Button>
        </PrivateContent>
        <Button
          onMouseEnter={() => setOpenedMenu('display')}
          onFocus={() => setOpenedMenu('display')}
          className={style.menuButton}
        >
          <Icon href={settingsIcon} className={style.menuButtonIcon} />
          <span>{t('top_bar-preferences-display')}</span>
          <Icon href={arrowIcon} className={style.arrowIcon} />
        </Button>
        {user.company.doc_visible && (
          <Button
            onClick={() => fetchDocUrl()}
            className={style.menuButton}
            onMouseEnter={() => setOpenedMenu(null)}
          >
            {docUrlStatus === 'LOADING' ? (
              <CircleLoader />
            ) : (
              <Icon href={exportIcon} className={style.menuButtonIcon} />
            )}
            {t('top_bar-documentation')}
          </Button>
        )}
        {role === 'admin' && (
          <Link to="/team-management" className={style.menuButton}>
            <Icon href={usersIcon} className={style.menuButtonIcon} />
            {t('top_bar-team_management')}
          </Link>
        )}
        <Button
          onClick={onLogout}
          className={style.menuButton}
          onMouseEnter={() => setOpenedMenu(null)}
          onFocus={() => setOpenedMenu(null)}
        >
          <Icon href={offIcon} className={style.logoutIcon} />
          {t('top_bar-logout')}
        </Button>
      </div>
      {openedMenu !== null && (
        <div className={style.rightPanel}>
          <div className={style.rightPanelTitle}>{t(`top_bar-preferences-${openedMenu}`)}</div>
          {openedMenu === 'display' && (
            <>
              {canSeeExternalProvidesVideogames(user.company.videogames) && (
                <PrivateContent feature="TOGGLE_EXTERNAL_PROVIDERS_MATCHES" userType={userType}>
                  <div className={style.userOption}>
                    <span className={style.userOptionLabel}>
                      {t('top_bar-external_providers_matches')}
                    </span>
                    <Switch
                      className={style.formatOptions}
                      selected={preferences.ebattles_display}
                      onChange={() =>
                        updatePreferences({ ebattles_display: !preferences.ebattles_display })
                      }
                      selectedColor="blue"
                    />
                  </div>
                </PrivateContent>
              )}
              <div className={classnames(style.userOption, style.multipleOptions)}>
                <span className={style.userOptionLabel}>{t('top_bar-odd_format_option')}</span>
                <RadioBlock
                  className={style.formatOptions}
                  value={preferences.odds_format}
                  options={oddsFormatOptions}
                  onChange={(odds_format: OddsFormat) => {
                    updatePreferences({ odds_format });
                  }}
                  selectedColor="blue"
                />
              </div>
              <div className={classnames(style.userOption, style.multipleOptions)}>
                <span className={style.userOptionLabel}>{t('top_bar-theme')}</span>
                <div className={style.themeOptions}>
                  <Button
                    onClick={() => updatePreferences({ theme: 'light' })}
                    className={classnames(style.themeOption, style.themeOptionLight, {
                      [style.themeOptionSelected]: preferences.theme === 'light'
                    })}
                  >
                    <Icon href={themeLightIcon} className={style.themeOptionIcon} />
                    {t('top_bar-theme-light')}
                    {preferences.theme === 'light' && (
                      <Icon href={checkIcon} className={style.checkIcon} />
                    )}
                  </Button>

                  <Button
                    onClick={() => updatePreferences({ theme: 'dark' })}
                    className={classnames(style.themeOption, style.themeOptionDark, {
                      [style.themeOptionSelected]: preferences.theme === 'dark'
                    })}
                  >
                    <Icon href={themeDarkIcon} className={style.themeOptionIcon} />
                    {t('top_bar-theme-dark')}
                    {preferences.theme === 'dark' && (
                      <Icon href={checkIcon} className={style.checkIcon} />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
          {openedMenu === 'trading' && (
            <>
              <PrivateContent feature="DISPLAY_OVERROUND" userType={userType}>
                <div className={style.userOption}>
                  <span className={style.userOptionLabel}>
                    {t('top_bar-display_overround_option')}
                  </span>
                  <Switch
                    className={style.formatOptions}
                    selected={!!preferences.overround_display}
                    onChange={overround_display => updatePreferences({ overround_display })}
                    selectedColor="blue"
                  />
                </div>
              </PrivateContent>
              <PrivateContent feature="COMPETITORS" userType={userType}>
                <>
                  <div className={style.userOption}>
                    <span className={style.userOptionLabel}>
                      {t('top_bar-display_competition')}
                    </span>
                    <Switch
                      className={style.formatOptions}
                      selected={!!preferences.competitors_display}
                      onChange={competitors_display => updatePreferences({ competitors_display })}
                      selectedColor="blue"
                    />
                  </div>
                  {preferences.competitors_display && (
                    <>
                      <div className={style.competitorsList}>
                        <div className={style.competitorsListTitle}>
                          {t('top_bar-display_scrapper')}
                        </div>
                        <div className={style.competitorsListItems}>
                          {TOGGLABLE_COMPETITORS.map(competitor => (
                            <Checkbox
                              className={style.competitorOption}
                              label={
                                <span className={style.competitorLabel}>
                                  <Icon
                                    className={style.competitorLogo}
                                    href={COMPETITORS_LOGO[competitor]}
                                  />
                                  {COMPETITORS_NAME[competitor]}
                                </span>
                              }
                              key={competitor}
                              status={
                                preferences.competitors_selected?.includes(competitor)
                                  ? 'checked'
                                  : 'unchecked'
                              }
                              onChange={() => {
                                if (preferences.competitors_selected) {
                                  updatePreferences({
                                    competitors_selected: toggleValueInArray(
                                      preferences.competitors_selected,
                                      competitor
                                    )
                                  });
                                }
                              }}
                              labelPosition="right"
                              size="small"
                            />
                          ))}
                        </div>
                      </div>
                      <div className={style.userOption}>
                        <Checkbox
                          className={style.competitorOption}
                          label={
                            <span className={style.competitorsListTitle}>
                              {t('top_bar-display_scrapper_winner_2_way_match')}
                            </span>
                          }
                          status={
                            preferences.competitors_display_match_winner_2_way
                              ? 'checked'
                              : 'unchecked'
                          }
                          onChange={() =>
                            updatePreferences({
                              competitors_display_match_winner_2_way:
                                !preferences.competitors_display_match_winner_2_way
                            })
                          }
                          labelPosition="right"
                          size="small"
                        />
                      </div>
                      <div className={style.userOption}>
                        <Checkbox
                          className={style.competitorOption}
                          label={
                            <span className={style.competitorsListTitle}>
                              {t('top_bar-display_prematch_scrapper_live')}
                            </span>
                          }
                          status={
                            preferences.competitors_display_prematch_during_live
                              ? 'checked'
                              : 'unchecked'
                          }
                          onChange={() =>
                            updatePreferences({
                              competitors_display_prematch_during_live:
                                !preferences.competitors_display_prematch_during_live
                            })
                          }
                          labelPosition="right"
                          size="small"
                        />
                      </div>
                    </>
                  )}
                </>
              </PrivateContent>
            </>
          )}
        </div>
      )}
    </Dropdown>
  );
};

UserDropdown.displayName = 'UserDropdown';

export default UserDropdown;
